import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Trash, ChatCircleText, LockSimple, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { AgentAvatar } from "@/components/agent/AgentCard";
import { api, errMsg, fmtDate } from "@/lib/api";

export default function ChatPage() {
  const { agentId } = useParams();
  const qc = useQueryClient();
  const [convId, setConvId] = useState(null);
  const { data: access, refetch: refetchAccess } = useQuery({ queryKey: ["access", agentId], queryFn: async () => (await api.get(`/agents/${agentId}/access`)).data });
  const isOwner = access?.role === "owner";
  const { data: agent, isError } = useQuery({
    queryKey: ["chat-agent", agentId, isOwner], enabled: !!access,
    queryFn: async () => (await api.get(isOwner ? `/agents/${agentId}` : `/agents/${agentId}/chat-info`)).data,
  });
  const { data: convs = [] } = useQuery({ queryKey: ["convs", agentId], enabled: !!access && access.role !== "none", queryFn: async () => (await api.get("/conversations", { params: { agent_id: agentId } })).data });

  const remove = async (id) => {
    try {
      await api.delete(`/conversations/${id}`);
      if (id === convId) setConvId(null);
      qc.invalidateQueries({ queryKey: ["convs", agentId] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (isError || access?.role === "none") return (
    <div className="flex min-h-screen flex-col items-start justify-center gap-4 bg-[#050505] px-12 text-white" data-testid="chat-no-access">
      <LockSimple size={32} className="text-zinc-500" /><p className="font-display text-3xl font-bold">No tienes acceso a este agente.</p>
      <Link to="/marketplace" className="btn-primary">Ir al marketplace</Link>
    </div>
  );
  if (!agent) return <div className="min-h-screen bg-[#050505]"><div className="h-px bg-white/10 thinking-beam" /></div>;
  const trialOut = access.role === "trial" && access.trial_remaining === 0;

  return (
    <div className="noise flex h-screen bg-[#050505] text-white">
      <aside className="hidden w-72 shrink-0 flex-col border-r border-white/10 bg-[#080808] md:flex">
        <div className="flex h-16 items-center gap-2 border-b border-white/10 px-4">
          <Link to={isOwner ? "/agents" : "/library"} className="p-2 text-zinc-400 hover:text-white" data-testid="chat-back-button"><ArrowLeft size={18} /></Link>
          <AgentAvatar icon={agent.icon} color={agent.color} size={30} />
          <p className="truncate text-sm font-semibold">{agent.name}</p>
        </div>
        <div className="p-3"><button onClick={() => setConvId(null)} className="btn-ghost w-full !justify-start !py-2" data-testid="chat-new-conversation"><Plus size={16} /> Nueva conversación</button></div>
        <div className="flex-1 overflow-y-auto px-3 pb-3" data-testid="chat-conversation-list">
          {convs.length === 0 && <p className="px-2 text-xs text-zinc-600">Sin conversaciones todavía.</p>}
          {convs.map((c) => (
            <div key={c.conversation_id} className={`group flex items-center gap-2 px-2 py-2 ${c.conversation_id === convId ? "bg-white/5" : "hover:bg-white/[0.03]"}`}>
              <button onClick={() => setConvId(c.conversation_id)} className="min-w-0 flex-1 text-left" data-testid={`chat-conversation-${c.conversation_id}`}>
                <p className="truncate text-sm text-zinc-200">{c.title}</p><p className="text-[11px] text-zinc-600">{fmtDate(c.updated_at)}</p>
              </button>
              <button onClick={() => remove(c.conversation_id)} className="p-1 text-zinc-600 opacity-0 hover:text-red-400 group-hover:opacity-100" data-testid={`chat-conversation-delete-${c.conversation_id}`}><Trash size={14} /></button>
            </div>
          ))}
        </div>
        {isOwner && <div className="border-t border-white/10 p-3"><Link to={`/agents/${agentId}/edit`} className="btn-ghost w-full !py-2" data-testid="chat-edit-agent"><PencilSimple size={15} /> Editar agente</Link></div>}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 items-center justify-between border-b border-white/10 px-4 md:hidden">
          <Link to="/library" className="text-zinc-400"><ArrowLeft size={18} /></Link>
          <button onClick={() => setConvId(null)} className="text-xs text-zinc-400" data-testid="chat-mobile-new"><ChatCircleText size={18} /></button>
        </div>
        {access.role === "trial" && (
          <div className="flex items-center justify-between gap-3 border-b border-amber-500/20 bg-amber-500/5 px-5 py-2.5 text-sm" data-testid="chat-trial-banner">
            <span className="text-amber-300">Modo prueba · {access.trial_remaining} mensajes restantes</span>
            <Link to={`/agent/${agentId}`} className="font-semibold text-white underline underline-offset-4" data-testid="chat-trial-buy-link">Adquirir agente</Link>
          </div>
        )}
        <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col">
          <ChatPanel
            agent={agent}
            conversationId={convId}
            onConversationCreated={(id) => { setConvId(id); qc.invalidateQueries({ queryKey: ["convs", agentId] }); }}
            onMeta={() => access.role === "trial" && refetchAccess()}
            onBlocked={() => refetchAccess()}
            disabled={trialOut}
            disabledNode={<div className="flex items-center justify-between text-sm text-zinc-400"><span>Prueba agotada.</span><Link to={`/agent/${agentId}`} className="btn-primary !py-2">Desbloquear</Link></div>}
          />
        </div>
      </div>
    </div>
  );
}
